import moment from 'moment-timezone';
import * as PropTypes from 'prop-types';
import * as React from 'react';
import { connect } from 'react-redux';
import { hideLoadingScreen } from '../../actions/app-actions';
import { backToFilters, confirmQuickCheckoutAndComplete, quickCheckout } from '../../cart/actions/cart-actions';
import { ItemType } from '../../classes/item-type';
import { Level } from '../../classes/level';
import { Performance } from '../../classes/performance';
import Modal from '../../common/modal/modal';
import SeatFeePreviewModal from '../../common/modal/seat-fee-preview-modal';
import ModalState from '../../common/modal/modal-states';
import PromoCodeContainer from '../../common/promo-code/promo-code';
import { convertNumberListToString } from '../../helpers/array-helpers';
import { CartHelpers } from '../../helpers/cart-helpers';
import { getCurrentPerformanceLocalTime } from '../../helpers/date-helpers';
import { Html } from '../../helpers/html-helpers';
import { getSelectedTicketCount } from '../../helpers/price-type-helpers';
import SecondaryNav from '../../menu/components/secondary-nav';
import { IAppState } from '../../reducers/index';
import { clearAdditionalSeatRequest, removeSeatingOptionGroup, resetVenueLevels, selectPriceTypeTickets, selectPriceTypeTicketsWithSeatingOption, selectSeatingOptionQuantity, selectSeatingOptions, selectVenueLevel, toggleAisleAccess, toggleSeatSeparation } from '../actions/filter-actions';
import { fetchSeatMatches } from '../actions/filter-fetch';
import { clearPriceRange, selectMaxPrice, selectMinPrice } from '../actions/price-range-actions';
import { applyPromoCode, cancelPromoApply, emptyCart, removePromoCode } from '../actions/promo-actions';
import FilterPageHeaderContainer from '../components/filter-page-header-container';
import FilterResults from '../components/filter-result';
import PerformanceInfo from '../components/performance-info';
import PerformanceList from '../components/performance-list';
import PerformanceSelector from '../components/performance-selector-menu';
import PriceRangeFilter from '../components/price-range-filter';
import PriceTypeFilterContainer from '../components/price-type-filter-container';
import SeatingOptionFilter from '../components/seating-option-filter';
import VenueLevelFilter from '../components/venue-level-filter';
import { SeatPricingWithQuantity } from '../../classes/seat';
import { showPriceRangeFilters } from '../../helpers/state-helpers';


export interface IFilterState {
    showPerformanceSelector: boolean;
    showNoSeatMatchForQuickCheckoutError: ModalState;
    showOnlyADASeatsModal: ModalState;
    chooseMySeatsModal: ModalState;
    onlyGALevelsModal: ModalState;
    priceTypesInvalidModal: ModalState;
    quickCheckoutSpecialSeatingModalState: ModalState;
    lastFocused: any;
    modalFocusableElements: any[];
    showSeatFeePreviewModal: ModalState;
};

class Filter extends React.Component<IAppState, IFilterState>{
    static contextTypes = {
        router: PropTypes.object
    }

    hasFuturePerformances: boolean; //current production has more performances in the future

    get promoCodeNeeded() {
        const { appSettingsModel, promoCodeModel } = this.props;

        // If item isn't on sale because we need a promo code and don't have one, return true
        const itemNotAvailable = !appSettingsModel.itemOnSale || !appSettingsModel.itemAvailable;

        const requiresPromoToUnlock = itemNotAvailable && appSettingsModel.promoCodeOnly;
        const noPromoApplied = promoCodeModel.promoCodeId < 1;

        return noPromoApplied && requiresPromoToUnlock;
    }

    constructor(props: IAppState) {
        super(props);

        let currentLocalTime = getCurrentPerformanceLocalTime(props.appSettingsModel.performanceTimeZone);
        this.hasFuturePerformances = props.appSettingsModel.productionPerformances.some((perf: Performance) => {
            return moment(perf.date).diff(currentLocalTime) >= 0;
        });
        
        this.state = {
            showPerformanceSelector: props.appSettingsModel.enableDateChange && !props.appSettingsModel.itemOnSale && this.hasFuturePerformances && !this.promoCodeNeeded,
            showNoSeatMatchForQuickCheckoutError: ModalState.CLOSE,
            showOnlyADASeatsModal: props.appSettingsModel.onlyADASeatsMatchFilterGoToSeatmapModalTitle.length === 0
                && props.appSettingsModel.onlyADASeatsMatchFilterGoToSeatmapModalMessage.length === 0 ? ModalState.KEEP_CLOSED
                : ModalState.CLOSE,
            chooseMySeatsModal: (props.appSettingsModel.noSeatsMatchFilterGoToSeatmapModalTitle.length === 0
                && props.appSettingsModel.noSeatsMatchFilterGoToSeatmapModalMessage.length === 0) ? ModalState.KEEP_CLOSED
                : ModalState.CLOSE,
            lastFocused: null,
            onlyGALevelsModal: ModalState.CLOSE,
            quickCheckoutSpecialSeatingModalState: ModalState.CLOSE,
            priceTypesInvalidModal: ModalState.CLOSE,
            modalFocusableElements: null,
            showSeatFeePreviewModal: ModalState.CLOSE
        };

        if (getSelectedTicketCount(props.priceTypesModel) > 0) {
            props.dispatch(fetchSeatMatches());
        }

        this.props.dispatch(hideLoadingScreen());
    }

    componentDidMount() {
        this.packageQuantityCheck(this.props);
    }

    get canProceed(): boolean {
        var selectedTicketQuantity = getSelectedTicketCount(this.props.priceTypesModel);
        return selectedTicketQuantity <= this.props.appSettingsModel.maxTicketQuantity
            && (this.props.appSettingsModel.minTicketQuantity > 0 ? (selectedTicketQuantity >= this.props.appSettingsModel.minTicketQuantity)
                : true);
    }

    get canProceedToSeatmap(): boolean {
        let selectedLevels = this.props.venueLevelModel.venueLevels.filter(lvl => lvl.selected);
        if (selectedLevels.length == 0)
            return true;

        return !(selectedLevels.every(lvl => lvl.isGaLevel)
            && (selectedLevels.filter(lvl => lvl.isGaLevel).length !== this.props.venueLevelModel.venueLevels.length));
    }

    get disableQuickCheckoutCTA(): boolean {
        if (this.canProceed && this.props.seatMatchResultModel.loadedResults
            && this.props.seatMatchResultModel.numberOfTicketsAvailable > 0
            && CartHelpers.checkForcedMultipleReserve(this.props.appSettingsModel.forcePatronsReserveInMultiples, this.props.priceTypesModel.priceTypes) == 0
        ) {
            return false;
        }

        const multipleSeatsRequired = CartHelpers.checkForcedMultipleReserve(this.props.appSettingsModel.forcePatronsReserveInMultiples, this.props.priceTypesModel.priceTypes);

        // don't disable so we can show a modal instead
        if (this.props.appSettingsModel.priceTypeCriteriaNotReachedShowAsModal && multipleSeatsRequired > 0) {
            return false;
        }

        // This situation had been set as "disabled-clickable". Leaving it enabled for now, but open to revising.
        if (this.props.seatMatchResultModel.dataHasLoaded && !this.props.seatMatchResultModel.requesting && this.props.seatMatchResultModel.numberOfTicketsAvailable === 0) {
            return false;
        }

        return true;
    }

    get disableSeatmapCTA(): boolean {
        if (this.canProceed && this.canProceedToSeatmap) {
            return false;
        }

        return true;
    }

    packageQuantityCheck(props: IAppState) {
        var packageTicketQuantity = (window as any).packageTicketQuantity;
        const isSetQuantityPackage = props.appSettingsModel.itemType === ItemType.Flex || props.appSettingsModel.itemType === ItemType.Super;
        const quantityNotSetServerside = packageTicketQuantity === undefined || packageTicketQuantity.length === 0;
        const quantityNotSetClientside = props.priceTypesModel.priceTypes.filter(pt => pt.selectedQuantity > 0).length === 0;

        const isVariablePricingPackage = props.appSettingsModel.isVariablePriceType;
        if (isSetQuantityPackage && quantityNotSetServerside && quantityNotSetClientside && !isVariablePricingPackage) {
            this.context.router.history.push("/ticket-select");
        }
    }

    componentWillReceiveProps(props: IAppState) {
        this.packageQuantityCheck(props);

        let noSeatMatchedError = ModalState.CLOSE;

        if (this.state.showNoSeatMatchForQuickCheckoutError === ModalState.KEEP_CLOSED) {
            noSeatMatchedError = ModalState.KEEP_CLOSED;
        }

        let onlyADASeatsModalOpen = ModalState.CLOSE;

        if (this.state.showOnlyADASeatsModal === ModalState.KEEP_CLOSED) {
            onlyADASeatsModalOpen = ModalState.KEEP_CLOSED;
        } else if (props.seatMatchResultModel.dataHasLoaded && !props.seatMatchResultModel.requesting && props.seatMatchResultModel.onlyADASeats) {
            onlyADASeatsModalOpen = ModalState.OPEN;
        }

        if (props.seatMatchResultModel.numberOfTicketsAvailable > 0) {
            noSeatMatchedError = ModalState.CLOSE;
        }

        let quickCheckoutSpecialSeatingState = ModalState.CLOSE;

        if (this.state.quickCheckoutSpecialSeatingModalState === ModalState.KEEP_CLOSED) {
            quickCheckoutSpecialSeatingState = ModalState.KEEP_CLOSED;
        } else if (!props.seatMatchResultModel.quickCheckoutMessage || props.seatMatchResultModel.quickCheckoutMessage.length === 0) {
            quickCheckoutSpecialSeatingState = ModalState.CLOSE;
        } else {
            quickCheckoutSpecialSeatingState = ModalState.OPEN;
        }

        let showSeatFeeModalWhenComponentUpdates = null;

        if (onlyADASeatsModalOpen === ModalState.OPEN || quickCheckoutSpecialSeatingState === ModalState.OPEN) {
            showSeatFeeModalWhenComponentUpdates = ModalState.CLOSE;
        }

        this.setState({
            showNoSeatMatchForQuickCheckoutError: noSeatMatchedError,
            showOnlyADASeatsModal: onlyADASeatsModalOpen,
            chooseMySeatsModal: this.state.chooseMySeatsModal === ModalState.KEEP_CLOSED ?
                ModalState.KEEP_CLOSED
                : ModalState.CLOSE,
            lastFocused: null,
            modalFocusableElements: null,
            onlyGALevelsModal: ModalState.CLOSE,
            quickCheckoutSpecialSeatingModalState: quickCheckoutSpecialSeatingState,
            ...(showSeatFeeModalWhenComponentUpdates && { showSeatFeePreviewModal: showSeatFeeModalWhenComponentUpdates })
        });
    }

    render() {
        const { itemType } = this.props.appSettingsModel;
        const requiresMultiSelect = (itemType === ItemType.Flex && !this.props.appSettingsModel.isVariablePriceType) || itemType === ItemType.Super;

        const cartContainsItems = this.props.appSettingsModel.cartCount > 0;

        const showPromoCode = (this.props.appSettingsModel.itemOnSale || this.props.appSettingsModel.promoCodeOnly)
            && itemType !== ItemType.ExchangeAddOn
            //&& itemType !== ItemType.Flex
            //&& itemType !== ItemType.Super
            && this.props.appSettingsModel.enablePromoCodes
            && !requiresMultiSelect
            && !cartContainsItems
            && (itemType !== ItemType.Flex || this.props.appSettingsModel.currentItemIndex < 2);

        const multipleSeatsRequired = CartHelpers.checkForcedMultipleReserve(this.props.appSettingsModel.forcePatronsReserveInMultiples, this.props.priceTypesModel.priceTypes);

        return (
            <>
                <main>                    
                    {

                        (this.props.appSettingsModel.itemType !== ItemType.Super && (this.props.appSettingsModel.performanceDateString !== null
                            || this.props.appSettingsModel.venueName.length > 0)) ?
                            <SecondaryNav performanceDate={this.props.appSettingsModel.performanceDateString} performanceTime={this.props.appSettingsModel.performanceTimeString} performanceVenue={this.props.appSettingsModel.venueName} />
                            : null
                    }

                    <div className="filters-container" id="main-content" tabIndex={-1}>

                        <FilterPageHeaderContainer {...this.props.appSettingsModel} />

                        <PerformanceInfo {...this.props.appSettingsModel}
                            performanceDateString={this.props.appSettingsModel.performanceDateString}
                            performanceTimeString={this.props.appSettingsModel.performanceTimeString}
                            onChangeDateClick={this._onChangePerformanceDateClick}
                            showComponent={itemType !== ItemType.Fixed && itemType !== ItemType.Super}
                            hideDateChange={itemType === ItemType.Flex || !this.props.appSettingsModel.enableDateChange
                                || this.props.appSettingsModel.productionPerformances.length <= 1} />

                        <PerformanceList performances={this.props.appSettingsModel.packagePerformances}
                            performancesHeaderTitle={this.props.appSettingsModel.performanceListFilterTitle}
                            altText={this.props.appSettingsModel.altText}
                            showPerformancesOnLoad={this.props.appSettingsModel.showPackagePerformancesOnLoad}
                            showComponent={itemType === ItemType.Fixed || itemType === ItemType.Super}
                            performancesSectionDescription={this.props.appSettingsModel.performanceListMessage} />

                        {this.promoCodeNeeded && this.renderNoPricing()}

                        {
                            showPromoCode ?
                                <PromoCodeContainer collapseDown={true} culture={this.props.appSettingsModel.currentCulture} />
                                : cartContainsItems && this.props.appSettingsModel.showDisabledPromoMessage ?
                                    <PromoCodeContainer collapseDown={true} culture={this.props.appSettingsModel.currentCulture} showMOSMessage={true} MOSMessage={this.props.appSettingsModel.disabledPromoMessage} />
                                    : null
                        }

                        {this.renderPriceInfo(requiresMultiSelect, multipleSeatsRequired)}

                    </div>
                </main>
                {
                    !this.props.appSettingsModel.itemOnSale && !this.hasFuturePerformances ?
                        <Modal modalKey="all performances passed" isError={true}
                            title={this.props.appSettingsModel.allPerformancesPassedModalTitle}
                            message={this.props.appSettingsModel.allPerformancesPassedModalMessage}
                            confirmFunction={this._performanceNotOnSaleRedirect} confirmString={this.props.appSettingsModel.modalCancelButtonText}
                            closeFunction={this._performanceNotOnSaleRedirect}
                            priority={1}
                        />
                        : null
                }

                {
                    !this.promoCodeNeeded && this._itemNotAvailableModal()
                }

                {
                    this._quickCheckoutNoticeModal()
                }

                {
                    this._onlyADASeatsNoticeModal()
                }

                {
                    this._quickCheckoutSpecialSeatingNoticeModal()
                }

                {
                    this.state.showPerformanceSelector ?
                        <PerformanceSelector title={this.props.appSettingsModel.changeDateSelectionHeader}
                            productionPerformances={this.props.appSettingsModel.productionPerformances}
                            isCancelable={true} closeModalFunction={this._hidePerformanceSelector.bind(this)}
                            appSettingsModel={this.props.appSettingsModel}>
                        </PerformanceSelector>
                        : null
                }

                {
                    this.state.chooseMySeatsModal === ModalState.OPEN ?
                        <Modal modalKey="no seats match filter proceed to seat map" isError={false} title={this.props.appSettingsModel.noSeatsMatchFilterGoToSeatmapModalTitle} message={this.props.appSettingsModel.noSeatsMatchFilterGoToSeatmapModalMessage}
                            confirmFunction={this._clearFiltersAndProceedToSeatmap.bind(this)}
                            confirmString={this.props.appSettingsModel.noSeatsMatchFilterGoToSeatmapModalProceed}
                            cancelFunction={this._closeChooseMySeatsModal.bind(this)}
                            cancelString={this.props.appSettingsModel.noSeatsMatchFilterGoToSeatmapModalCancel}
                            closeFunction={this._closeChooseMySeatsModal.bind(this)}
                        />
                        : null
                }

                {
                    this.state.onlyGALevelsModal === ModalState.OPEN ?
                        <Modal modalKey={"only GA levels"} isError={false} title={this.props.appSettingsModel.onlyGALevelsSelectedGoToSeatmapModalTitle} message={this.props.appSettingsModel.onlyGALevelsSelectedGoToSeatmapModalMessage}
                            confirmFunction={this._proceedToSeatmap.bind(this)}
                            confirmString={this.props.appSettingsModel.onlyGALevelsSelectedGoToSeatmapModalProceed}
                            cancelFunction={this._closeOnlyGALevelsModal.bind(this)}
                            closeFunction={this._closeOnlyGALevelsModal.bind(this)}
                            cancelString={this.props.appSettingsModel.onlyGALevelsSelectedGoToSeatmapModalCancel} />
                        : null
                }

                {
                    this.state.priceTypesInvalidModal === ModalState.OPEN ?
                        <Modal modalKey={"price types invalid"} isError={true} title=""
                            message={this.props.appSettingsModel.forceMultipleFilterMessageText
                                .replace("{0}", multipleSeatsRequired.toString())}
                            confirmFunction={this._closePriceTypeInvalidModal.bind(this)}
                            confirmString={this.props.appSettingsModel.modalConfirmButtonText}
                            closeFunction={this._closePriceTypeInvalidModal.bind(this)} />
                        : null
                }

                {
                    this.props.appSettingsModel.maxTicketQuantity == 0
                        ?
                        <Modal modalKey="ticket quantity zero"
                            isError={true}
                            title={this.props.appSettingsModel.ticketQuantityZeroModalTitle}
                            message={this.props.appSettingsModel.ticketQuantityZeroModalMessage}
                            confirmFunction={this._ticketQuantityZeroRedirect.bind(this)}
                            confirmString={this.props.appSettingsModel.ticketQuantityZeroRedirectButtonText}
                            closeFunction={this._ticketQuantityZeroRedirect.bind(this)}
                        />
                        : null
                }

                {
                    this.props.appSettingsModel.displaySeatFee && this.state.showSeatFeePreviewModal === ModalState.OPEN ?
                        <SeatFeePreviewModal modalKey="preview seats with fees"
                            title={this.props.appSettingsModel.seatFeePreviewTitle}
                            message={this.props.appSettingsModel.seatFeePreviewMessage}
                            confirmFunction={this._confirmSeatFeePreviewModal.bind(this)}
                            cancelFunction={this._closeSeatFeePreviewModal.bind(this)}
                            goToSeatmapFunction={this._proceedToSeatmap.bind(this)}
                            selectedPricing={this.getBestSelectedPrices()}
                            currencyFormat={this.props.appSettingsModel.currencyFormat}
                            ticketTypeFormat={this.props.appSettingsModel.seatFeePreviewTicketTypeFormat}
                            priceTypeTextHeadingFormat={this.props.appSettingsModel.priceTypeTextHeadingFormat}
                            cancelString={this.props.appSettingsModel.seatFeePreviewCancelText}
                            goToSeatmapString={this.props.appSettingsModel.seatFeePreviewSeatmapText}
                            confirmString={this.props.appSettingsModel.seatFeePreviewConfirmText}
                            ticketCostText={this.props.appSettingsModel.ticketCostText}
                            feeAmountText={this.props.appSettingsModel.feeAmountText}
                            loadedResults={this.props.seatMatchResultModel.loadedResults}
                            requesting={this.props.seatMatchResultModel.requesting}
                            loadingText={this.props.appSettingsModel.loadingText}
                            loadingSvgText={this.props.appSettingsModel.loadingSvgText}
                            isBestAvailableOnly={this.props.appSettingsModel.isGeneralAdmissionVenue}
                        />
                        : null
                }

                {
                    this.props.appSettingsModel.itemOnSale ?
                        this.renderFooter()
                        : null
                }
            </>

        );
    }

    getBestSelectedPrices = (): SeatPricingWithQuantity[] => {
        const selectedZoneId = this.props.seatMatchResultModel.suggestedSeats[0].zoneId;
        const selectedPriceTypes = this.props.priceTypesModel.priceTypes
            .filter(priceType => { return priceType.selectedQuantity > 0 })

        const selectedZone = this.props.appSettingsModel.seatZones
            .filter(zone => { return zone.zoneId == selectedZoneId })[0];

        //TODO: allow multiple zones
        const prices = selectedZone.prices
            .filter(price => {
                return selectedPriceTypes.some(priceType => priceType.priceTypeId == price.priceTypeId)
            });

        let pricesWithQuantities = [];
        prices.forEach(price => {
            const priceWithQuantity: SeatPricingWithQuantity = {
                ...price,
                zoneDescription: selectedZone.description,
                quantity: selectedPriceTypes.filter(priceType => priceType.priceTypeId === price.priceTypeId)[0].selectedQuantity
            };
            pricesWithQuantities.push(priceWithQuantity);
        })

        return pricesWithQuantities;
    }

    renderNoPricing = () => {
        return (
            <section className="filters-section">
                {Html.renderDiv(this.props.appSettingsModel.filterPageNoPricingMessage)}
            </section>
        )
    }

    renderPriceInfo = (requiresMultiSelect: boolean, multipleSeatsRequired: number) => {
        if (this.promoCodeNeeded) {
            return;
        }

        const moveSpecialSeatingSection: boolean = this.props.appSettingsModel.renderAdditionalSeatingOptionsBelowTicketQuantity;
        const { itemType } = this.props.appSettingsModel;

        return (
            <>
                <PriceTypeFilterContainer
                    priceTypes={this.props.priceTypesModel.priceTypes}
                    onChangePriceTypeQuantity={this._onChangePriceTypeQuantity}
                    appSettingsModel={this.props.appSettingsModel}
                    showComponent={this.props.appSettingsModel.itemOnSale && !requiresMultiSelect}
                    multipleOfSeatsRequiredToContinue={multipleSeatsRequired}
                />

                {
                    moveSpecialSeatingSection ? <SeatingOptionFilter onToggleSeatSeparation={this._onToggleSeatSeparation}
                        onToggleAisleAccess={this._onToggleAisleAccess}
                        {...this.props.additionalSeatingOptionModel}
                        allowAisleAccessSelected={this.props.additionalSeatingOptionModel.allowAisleAccessSelected}
                        removeSeatingOptionGroup={this._onRemoveSeatingOptionGroup}
                        selectedTicketQuantity={getSelectedTicketCount(this.props.priceTypesModel)}
                        onAdditionalSeatRequestClear={this._onAdditionalSeatRequestClear}
                        onSelectSeatingOption={this._onSelectSeatingOption}
                        onSelectSeatingOptionQuantity={this._onSelectSeatingOptionQuantity}
                        appSettings={this.props.appSettingsModel}
                        showComponent={this.props.appSettingsModel.itemOnSale}
                        showAdditionalNotes={this.props.appSettingsModel.renderAdditionalNotes}
                    />
                        : null
                }
                <PriceRangeFilter {...this.props.priceRangeModel}
                    {...this.props.appSettingsModel}
                    onSelectMinPrice={this._onSelectMinPrice}
                    onSelectMaxPrice={this._onSelectMaxPrice}
                    onPriceRangeClear={this._onClearPriceRange}
                    startingMaxPrice={this.props.priceRangeModel.maxPrice}
                    startingMinPrice={this.props.priceRangeModel.minPrice}
                    showComponent={this.props.appSettingsModel.itemOnSale && (this.props.priceTypesModel.priceTypes.length > 1
                        || this.props.priceRangeModel.lowerRangeLimit !== this.props.priceRangeModel.upperRangeLimit) && showPriceRangeFilters(this.props.priceRangeModel)} />

                <VenueLevelFilter levels={this.props.venueLevelModel.venueLevels}
                    {...this.props.appSettingsModel}
                    onResetLevels={this._onResetVenueLevel}
                    onSelectLevel={this._onSelectVenueLevel}
                    dispatch={this.props.dispatch}
                    selectedPriceTypeIdsString={convertNumberListToString(this.props.priceTypesModel.priceTypes.filter(pt => pt.selectedQuantity > 0).map(pt => pt.priceTypeId))}
                    showComponent={this.props.appSettingsModel.itemOnSale && this.props.appSettingsModel.showLevelFilter}
                    selectedLevelString={this.props.venueLevelModel.venueLevels.filter(level => {
                        return level.selected;
                    }).map(level => {
                        return level.levelNumber;
                    }).toString()}
                    isCheckbox={true}
                    displaySeatFee={this.props.appSettingsModel.displaySeatFee}                    
                />

                {
                    !moveSpecialSeatingSection ? <SeatingOptionFilter onToggleSeatSeparation={this._onToggleSeatSeparation}
                        onToggleAisleAccess={this._onToggleAisleAccess}
                        {...this.props.additionalSeatingOptionModel}
                        allowAisleAccessSelected={this.props.additionalSeatingOptionModel.allowAisleAccessSelected}
                        removeSeatingOptionGroup={this._onRemoveSeatingOptionGroup}
                        selectedTicketQuantity={getSelectedTicketCount(this.props.priceTypesModel)}
                        onAdditionalSeatRequestClear={this._onAdditionalSeatRequestClear}
                        onSelectSeatingOption={this._onSelectSeatingOption}
                        onSelectSeatingOptionQuantity={this._onSelectSeatingOptionQuantity}
                        appSettings={this.props.appSettingsModel}
                        showComponent={this.props.appSettingsModel.itemOnSale}
                        showAdditionalNotes={this.props.appSettingsModel.renderAdditionalNotes}
                    />
                        : null
                }
            </>
        )
    }

    renderFooter = () => {
        if (this.promoCodeNeeded) {
            return;
        }

        return (
            <footer className="footer-wrapper">
                <FilterResults loadedResults={this.props.seatMatchResultModel.loadedResults}
                    numberOfTicketOptions={this.props.seatMatchResultModel.numberOfTicketOptions}
                    requesting={this.props.seatMatchResultModel.requesting}
                    filterSearchingToast={this.props.appSettingsModel.filterSearchingToast} />

                <div className="footer-container">
                    {
                        !this.props.appSettingsModel.isGeneralAdmissionVenue ?
                            <button type="button" className={"footer-link"} disabled={this.disableSeatmapCTA}
                                onClick={() => this._onProceedClick()}>{this.props.appSettingsModel.filterProceedButton}</button>
                            : null
                    }
                    <button type="button" className={"footer-link"} disabled={this.disableQuickCheckoutCTA}
                        onClick={this._onQuickCheckoutClick.bind(this)}>{this.props.appSettingsModel.filterQuickCheckoutButton}</button>
                </div>
            </footer>
        )
    }

    _closeChooseMySeatsModal() {
        this.setState({
            chooseMySeatsModal: ModalState.CLOSE
        });
    }

    _closeOnlyGALevelsModal() {
        this.setState({
            onlyGALevelsModal: ModalState.CLOSE
        });
    }

    _closePriceTypeInvalidModal() {
        this.setState({
            priceTypesInvalidModal: ModalState.CLOSE
        });
    }

    _onProceedClick() {
        // If there are 0 seats matched, show a modal letting them know.
        if (this.props.seatMatchResultModel.numberOfTicketsAvailable === 0
            && this.state.chooseMySeatsModal !== ModalState.KEEP_CLOSED) {
            this.setState({
                chooseMySeatsModal: ModalState.OPEN
            });
        } else if (!this.canProceedToSeatmap) {
            this.setState({
                onlyGALevelsModal: ModalState.OPEN
            });
        }
        else if (CartHelpers.checkForcedMultipleReserve(this.props.appSettingsModel.forcePatronsReserveInMultiples, this.props.priceTypesModel.priceTypes) > 0) {
            this.setState({
                priceTypesInvalidModal: ModalState.OPEN
            });
        }
        else {
            this._proceedToSeatmap();
        }
    }

    _clearFiltersAndProceedToSeatmap() {
        this._onAdditionalSeatRequestClear();
        return this._proceedToSeatmap();
    }

    _proceedToSeatmap() {
        this.context.router.history.push(`/seatmap${this.props.location.search}`);
    }

    _closeSpecialSeatingModalAndBackToFilters() {
        this.props.dispatch(backToFilters());
        this._closeQuickCheckoutSpecialSeatingNoticeModal();
    }

    _closeQuickCheckoutSpecialSeatingNoticeModal() {
        this.setState({
            quickCheckoutSpecialSeatingModalState: ModalState.CLOSE
        });
    }

    _quickCheckoutSpecialSeatingNoticeModal(): JSX.Element {
        switch (this.state.quickCheckoutSpecialSeatingModalState) {
            case ModalState.OPEN:
            case ModalState.KEEP_OPEN:
                return (
                    <Modal modalKey="missing attestation" title={this.props.appSettingsModel.missingAttestationErrorMessageHeader}
                        message={this.props.seatMatchResultModel.quickCheckoutMessage}
                        cancelString={this.props.appSettingsModel.modalCancelButtonText}
                        cancelFunction={this._closeSpecialSeatingModalAndBackToFilters.bind(this)}
                        confirmFunction={this._quickCheckout.bind(this)}
                        confirmString={this.props.appSettingsModel.modalConfirmButtonText}
                        closeFunction={this._closeSpecialSeatingModalAndBackToFilters.bind(this)}
                    />
                );
            default:
                return null;
        }
    }

    _quickCheckoutNoticeModal(): JSX.Element {
        switch (this.state.showNoSeatMatchForQuickCheckoutError) {
            case ModalState.OPEN:
            case ModalState.KEEP_OPEN:
                return (
                    <Modal modalKey="no seats match quick checkout"
                        title={this.props.appSettingsModel.noSeatsMatchFilterQuickCheckoutModalTitle}
                        message={this.props.appSettingsModel.noSeatsMatchFilterQuickCheckoutModalMessage}
                        confirmString={this.props.appSettingsModel.modalConfirmButtonText}
                        confirmFunction={this._closeQuickCheckoutModal}
                        closeFunction={this._closeQuickCheckoutModal}
                    />
                );
            default:
                return null;
        };
    }

    _itemNotAvailableModal(): JSX.Element {
        return (
            this.props.appSettingsModel.itemAvailable ?
                null :
                <Modal modalKey="item not available (no available seats/price types)"
                    title={this.props.appSettingsModel.performanceSoldOutModalTitle}
                    message={this.props.appSettingsModel.performanceSoldOutModalMessage}
                    confirmString={this.props.appSettingsModel.performanceNotOnSaleRedirectButtonText}
                    confirmFunction={this._itemNotAvailableRedirect}
                />
        );
    }

    _onlyADASeatsNoticeModal(): JSX.Element {
        switch (this.state.showOnlyADASeatsModal) {
            case ModalState.OPEN:
            case ModalState.KEEP_OPEN:
                return (
                    <Modal modalKey="only ADA seats match filters"
                        title={this.props.appSettingsModel.onlyADASeatsMatchFilterGoToSeatmapModalTitle}
                        message={this.props.appSettingsModel.onlyADASeatsMatchFilterGoToSeatmapModalMessage}
                        confirmString={this.props.appSettingsModel.modalConfirmButtonText}
                        confirmFunction={this._closeADAOnlyModal.bind(this)}
                        closeFunction={this._closeADAOnlyModal.bind(this)}
                    />
                );
            default:
                return null;
        };
    }

    _onChangePerformanceDateClick = () => {
        this._showPerformanceSelector();
    };

    _showPerformanceSelector() {
        let lastFocused = document.activeElement;

        this.setState({
            showPerformanceSelector: true,
            lastFocused: lastFocused,
        });
        (document.activeElement as any).blur();
    }

    _hidePerformanceSelector() {
        this.setState({
            showPerformanceSelector: false,
        });

        this.state.lastFocused.focus();
    }

    _performanceNotOnSaleRedirect = () => {
        window.location.href = this.props.appSettingsModel.performanceNotOnSaleRedirectUrl;
    }

    _ticketQuantityZeroRedirect() {
        window.location.href = this.props.appSettingsModel.ticketQuantityZeroRedirectUrl;
    }

    _onSelectVenueLevel = (level: Level) => {
        this.props.dispatch(selectVenueLevel(level.levelNumber));
    };

    _onResetVenueLevel() {
        this.props.dispatch(resetVenueLevels());
    }

    _onSelectSeatingOptionQuantity = (seatingOptionGroupId: string, quantity: number) => {
        this.props.dispatch(selectSeatingOptionQuantity(seatingOptionGroupId, quantity));
    }

    _onSelectSeatingOption = (seatingOptionGroupId: string, seatingOptions: Array<any>, seatingOptionToggle: boolean) => {
        this.props.dispatch(selectSeatingOptions(seatingOptionGroupId, seatingOptions, seatingOptionToggle));
    };

    _onToggleSeatSeparation = (evt) => {
        this.props.dispatch(toggleSeatSeparation(evt.target.checked));
    };

    _onChangePriceTypeQuantity = (quantity: number, priceTypeId: number) => {
        var attachedSpecialSeatingRequest = this.props.appSettingsModel.priceTypeSettings.filter(pt => pt.priceTypeId === priceTypeId)[0].attachedSeatingOptionGroup;
        if (attachedSpecialSeatingRequest != null) {
            this.props.dispatch(selectPriceTypeTicketsWithSeatingOption(priceTypeId, attachedSpecialSeatingRequest.id, quantity, false, [], true));
        } else {
            this.props.dispatch(selectPriceTypeTickets(priceTypeId, quantity, false, [], true));
        }
    };

    _onApplyPromoClick = (promoCode) => {
        this.props.dispatch(applyPromoCode(promoCode, this.props.appSettingsModel.currentCulture, true));
    };

    _onCancelPromoClick = () => {
        this.props.dispatch(cancelPromoApply());
    };

    _onRemovePromoClick = () => {
        this.props.dispatch(removePromoCode(this.props.appSettingsModel.currentCulture, true));
    };

    _onEmptyCartClick = (promoCode) => {
        this.props.dispatch(emptyCart(promoCode, this.props.appSettingsModel.currentCulture));
    };

    _onClearPriceRange = (minPrice, maxPrice) => {
        this.props.dispatch(clearPriceRange(minPrice, maxPrice))
    };

    _onSelectMinPrice = (minPrice: number) => {
        this.props.dispatch(selectMinPrice(minPrice));
    };

    _onSelectMaxPrice = (maxPrice: number) => {
        this.props.dispatch(selectMaxPrice(maxPrice))
    };

    _onToggleAisleAccess = (evt) => {
        this.props.dispatch(toggleAisleAccess(evt.target.checked));
    }

    _onRemoveSeatingOptionGroup = (seatingOptionGroupId: string) => {
        var companionSeatingOptions = this.props.additionalSeatingOptionModel.additionalSeatingOptions.filter(seatingOption => seatingOption.unlockSeatingOptionGroupId !== null && seatingOption.unlockSeatingOptionGroupId === seatingOptionGroupId);

        companionSeatingOptions.forEach(companionSeating => {
            this.props.dispatch(removeSeatingOptionGroup(companionSeating.id));
        });

        this.props.dispatch(removeSeatingOptionGroup(seatingOptionGroupId));
    }

    _onAdditionalSeatRequestClear = () => {
        this.props.dispatch(clearAdditionalSeatRequest());
    };

    _onQuickCheckoutClick() {
        if (this.disableQuickCheckoutCTA) {
            this.setState({
                showNoSeatMatchForQuickCheckoutError: ModalState.OPEN
            });
        }       
        else {
            const multipleSeatsRequired = CartHelpers.checkForcedMultipleReserve(this.props.appSettingsModel.forcePatronsReserveInMultiples, this.props.priceTypesModel.priceTypes);

            if (this.props.appSettingsModel.priceTypeCriteriaNotReachedShowAsModal && multipleSeatsRequired > 0) {
                this.setState({
                    priceTypesInvalidModal: ModalState.OPEN
                })
            }
            else if (this.state.showNoSeatMatchForQuickCheckoutError !== ModalState.KEEP_CLOSED && this.props.seatMatchResultModel.dataHasLoaded && !this.props.seatMatchResultModel.requesting && this.props.seatMatchResultModel.numberOfTicketsAvailable === 0) {

                this.setState({
                    showNoSeatMatchForQuickCheckoutError: ModalState.OPEN
                })
            }
            /*if seat fees should be displayed, do this instead of quick checkout*/
            else if (this.props.appSettingsModel.displaySeatFee) {
                this.props.dispatch(fetchSeatMatches(true));
                this.setState({
                    showSeatFeePreviewModal: ModalState.OPEN
                });
            }
            else {
                this.props.dispatch(confirmQuickCheckoutAndComplete());
            }
        }
    }

    _quickCheckout() {
        this._closeQuickCheckoutSpecialSeatingNoticeModal();
        this.props.dispatch(quickCheckout());
    }

    _closeQuickCheckoutModal = () => {
        this.setState({
            showNoSeatMatchForQuickCheckoutError: ModalState.KEEP_CLOSED
        });
    }

    _itemNotAvailableRedirect = () => {
        window.location.href = this.props.appSettingsModel.performanceNotOnSaleRedirectUrl;
    }

    _closeADAOnlyModal() {
        this.setState({
            showOnlyADASeatsModal: ModalState.KEEP_CLOSED
        });
    }

    _confirmSeatFeePreviewModal() {
        /*assuming we'll probably need to do some other stuff here when someone confirms they want to quick checkout with their seats*/
        this.props.dispatch(confirmQuickCheckoutAndComplete());
    }

    _closeSeatFeePreviewModal() {
        this.setState({
            showSeatFeePreviewModal: ModalState.CLOSE
        });
    }
}

export default connect((state) => state)(Filter);